import { makeAutoObservable, runInAction } from 'mobx'
import { readJSON, writeJSON } from '@/lib/storage'
import { authService, meService } from '@/services'
import { balanceGroupsService, type BalanceGroup } from '@/services/finance/balanceGroups.service'
import { setSentryUser } from '@/lib/sentry'
import { clearQueryCache } from './queryCache'
import { toMenuAreas, type MenuArea } from '@/layout/legacyMenu'
import type { AccessResource, AuthProfile, LinkedCompany, MeContext, UserBranch } from '@/types/api'

/**
 * Holds GET /v1/me/context. Navigation and control visibility derive from it;
 * the server stays authoritative for every request. Never branch on role name.
 */
export class SessionStore {
  context: MeContext | null = null
  activeBranchId: string | null = null
  /** The profile photo from /me/profile; empty until it loads, when there is none, or when the request fails. */
  avatarUrl = ''
  /** The companies this sign-in can switch to (login response, then GET /me/companies). */
  companies: LinkedCompany[] = []
  /** «Балансы» (ChF, AFF, IML, CAMA, UZB LOG); empty when the profile cannot read them. */
  balanceGroups: BalanceGroup[] = []
  /** The header «Баланс» switch: lists and new documents of that balance; '' = all. */
  activeBalanceGroupId = ''
  /** The pages' «AFF | ChF» toggle («Материнская компания»); remembered per user, see parentGroupId. */
  activeParentGroupId = ''
  loading = false

  constructor() {
    makeAutoObservable(this)
  }

  get profile(): AuthProfile | null {
    return this.context?.profile ?? null
  }

  get branches(): UserBranch[] {
    return this.context?.branches ?? []
  }

  get activeBranch(): UserBranch | null {
    return this.branches.find((branch) => branch.id === this.activeBranchId) ?? null
  }

  get currentCompany(): LinkedCompany | null {
    return this.companies.find((company) => company.is_current) ?? null
  }

  /** The switcher shows when the server says so or the sign-in matched more than one company. */
  get canSwitchCompany() {
    return this.context?.can_switch_company === true || this.companies.length > 1
  }

  get isPlatformAdmin() {
    return this.context?.is_platform_admin === true
  }

  get capabilities(): Set<string> {
    return new Set(this.context?.capabilities ?? [])
  }

  /** The admin menu the server lets this profile see (menu_items narrowed by its rights). */
  get menuAreas(): MenuArea[] {
    return toMenuAreas(this.context?.menu ?? [])
  }

  /** «path#key» of the tabs and buttons the role does not show. */
  get hiddenControls(): Set<string> {
    return new Set((this.context?.hidden_controls ?? []).map((control) => `${control.path}#${control.key}`))
  }

  /**
   * Whether the role shows the tab or button `key` of the page at `pathname`
   * (its detail routes included). A key the menu does not list is shown.
   */
  allows = (pathname: string, key: string) => {
    if (this.hiddenControls.size === 0) return true
    for (const control of this.context?.hidden_controls ?? []) {
      if (control.key === key && (pathname === control.path || pathname.startsWith(`${control.path}/`))) return false
    }
    return true
  }

  get resources(): AccessResource[] {
    return this.context?.resources ?? []
  }

  get resourceCodes(): Set<string> {
    return new Set(this.resources.map((resource) => resource.code))
  }

  can = (capability?: string | null) => {
    if (!capability) return true
    return this.capabilities.has(capability)
  }

  canAny = (...capabilities: string[]) => capabilities.some((capability) => this.capabilities.has(capability))

  /** field_access: resource-code -> action -> true */
  fieldAllowed = (resourceCode: string, action: string) =>
    this.context?.field_access?.[resourceCode]?.[action] === true

  /** `companies` comes with a sign-in; without it (a reload) they are fetched in the background. */
  async load(companies?: LinkedCompany[]) {
    this.loading = true
    try {
      const context = await meService.context()
      runInAction(() => {
        this.context = context
        this.activeBranchId = this.pickBranch(context)
        if (companies) this.companies = companies
      })
      setSentryUser(context.profile.id)
      void this.loadAvatar()
      // A trader's or logist's account opens fixed to its balance.
      runInAction(() => {
        this.activeBalanceGroupId = context.balance_group_id ?? ''
      })
      void this.loadBalanceGroups(context)
      if (!companies) void this.loadCompanies()
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  /** /me/context carries no photo; a failed read keeps the initials instead of failing the session. */
  private async loadAvatar() {
    try {
      const profile = await meService.profile()
      runInAction(() => {
        // Signed out or switched profile while it loaded: not ours to show.
        if (this.context?.profile.id === profile.id) this.avatarUrl = profile.avatar_url ?? ''
      })
    } catch {
      runInAction(() => {
        this.avatarUrl = ''
      })
    }
  }

  /** The own photo was just saved: the header shows it without reloading the session. */
  setAvatarUrl(url: string) {
    this.avatarUrl = url
  }

  /** A failed read only hides the switcher; the session itself is unaffected. */
  private async loadCompanies() {
    try {
      const companies = await authService.companies()
      runInAction(() => {
        if (this.context) this.companies = companies
      })
    } catch {
      /* keep what is known */
    }
  }

  /** A failed or forbidden read only hides the switch. */
  private async loadBalanceGroups(context: MeContext) {
    if (!(context.capabilities ?? []).includes('finance_balance_groups.read')) return
    try {
      const groups = await balanceGroupsService.all()
      runInAction(() => {
        if (this.context?.profile.id !== context.profile.id) return
        this.balanceGroups = groups
        this.activeParentGroupId = readJSON<string>(`mf.parent.${context.profile.id}`, '')
        if (context.balance_group_id) return
        const remembered = readJSON<string>(`mf.balance.${context.profile.id}`, '')
        this.activeBalanceGroupId = groups.some((group) => group.id === remembered) ? remembered : ''
      })
    } catch {
      /* keep the switch hidden */
    }
  }

  get activeBalanceGroup(): BalanceGroup | null {
    return this.balanceGroups.find((group) => group.id === this.activeBalanceGroupId) ?? null
  }

  /**
   * The balance that narrows firm and product pickers: an importer's or the
   * distributor's. A trader or logist buys for the importers, so their
   * pickers stay open ('' = no narrowing).
   */
  get ownerBalanceGroupId() {
    const kind = this.activeBalanceGroup?.kind
    return kind === 'trader' || kind === 'logist' ? '' : this.activeBalanceGroupId
  }

  /** The «AFF | ChF» choices: the importer balances, AFF first. Fewer than two: no toggle and no filter. */
  get parentGroups(): BalanceGroup[] {
    return this.balanceGroups
      .filter((group) => group.kind === 'importer')
      .sort((a, b) => Number(b.code.toUpperCase() === 'AFF') - Number(a.code.toUpperCase() === 'AFF') || a.sort_order - b.sort_order)
  }

  /**
   * The toggle's parent company, the `parent_group_id` the listed pages send:
   * the user's remembered choice, else the session's own parent (an importer
   * balance chosen in the header or fixed on the account), else AFF.
   * '' when there is nothing to choose between.
   */
  get parentGroupId(): string {
    const groups = this.parentGroups
    if (groups.length < 2) return ''
    for (const id of [this.activeParentGroupId, this.activeBalanceGroupId]) {
      if (id && groups.some((group) => group.id === id)) return id
    }
    return groups[0].id
  }

  setParentGroup(groupId: string) {
    if (!this.parentGroups.some((group) => group.id === groupId)) return
    this.activeParentGroupId = groupId
    if (this.profile) writeJSON(`mf.parent.${this.profile.id}`, groupId)
  }

  /** The account is fixed to one balance: the switch cannot move. */
  get balanceFixed() {
    return Boolean(this.context?.balance_group_id)
  }

  setActiveBalanceGroup(groupId: string) {
    if (this.balanceFixed) return
    if (groupId && !this.balanceGroups.some((group) => group.id === groupId)) return
    if (groupId !== this.activeBalanceGroupId) clearQueryCache()
    this.activeBalanceGroupId = groupId
    if (this.profile) writeJSON(`mf.balance.${this.profile.id}`, groupId)
  }

  /** The server refused a switch to it (COMPANY_NOT_LINKED): it is no longer offered. */
  forgetCompany(companyId: string) {
    this.companies = this.companies.filter((company) => company.id !== companyId)
  }

  /** Call after role, permission, profile, branch or status changes affecting the current user. */
  async reload() {
    await this.load()
  }

  setActiveBranch(branchId: string) {
    if (!this.branches.some((branch) => branch.id === branchId)) return
    // Cached lists belong to the previous branch's scope.
    if (branchId !== this.activeBranchId) clearQueryCache()
    this.activeBranchId = branchId
    if (this.profile) writeJSON(`mf.branch.${this.profile.id}`, branchId)
  }

  clear() {
    clearQueryCache()
    setSentryUser(null)
    this.context = null
    this.activeBranchId = null
    this.avatarUrl = ''
    this.companies = []
    this.balanceGroups = []
    this.activeBalanceGroupId = ''
    this.activeParentGroupId = ''
  }

  private pickBranch(context: MeContext): string | null {
    const permitted = new Set(context.branches.map((branch) => branch.id))
    const remembered = readJSON<string | null>(`mf.branch.${context.profile.id}`, null)
    if (remembered && permitted.has(remembered)) return remembered
    if (context.profile.default_branch_id && permitted.has(context.profile.default_branch_id)) {
      return context.profile.default_branch_id
    }
    return context.branches.find((branch) => branch.is_default)?.id ?? context.branches[0]?.id ?? null
  }
}
